import React from 'react';
import {
  Box, Button, Divider, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SettingsCard from './SettingsCard';
import { track } from '../analytics';

const ACTIVE_GREEN = '#2E7D32';

const BENEFITS = [
  'Trainer support via text and email',
  'Personalized learning content',
  'Community access',
  'Welcome Home Album',
];

const COMMUNITY_BENEFITS = [
  'Trainer support via text and email',
  'Personalized learning content',
];

type MembershipCardProps = {
  status?: string; // 'active' | 'expired'
  expiryDate?: string;
  planName?: string;
  isPremium?: boolean;
  /** Free, AWO-provided display: pre-surrender "Community Membership" or "Foster Membership". */
  variant?: 'community' | 'foster';
  /** AWO name shown as "Provided by ...". The variant is ignored without it. */
  providedBy?: string;
};

function MembershipCard({
  status, expiryDate, planName, isPremium, variant, providedBy,
}: MembershipCardProps) {
  const isProvided = !!variant && !!providedBy && !isPremium;
  let providedPlanName = '';
  if (isProvided) providedPlanName = variant === 'foster' ? 'Foster Membership' : 'Community Membership';
  const isExpired = status === 'expired';
  const toneFg = isExpired ? 'error.main' : ACTIVE_GREEN;
  const toneBg = isExpired ? 'rgba(176,0,32,0.10)' : 'rgba(46,125,50,0.10)';
  const expiryLabel = isExpired ? 'Expired' : 'Expires';

  return (
    <SettingsCard title="Membership">
      {/* Status row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.75, md: 2 },
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: '12px',
          p: { xs: '14px', md: '15px 18px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 600,
              fontSize: 11.5,
              letterSpacing: '0.3px',
              color: 'primary.main',
              bgcolor: 'primary.50',
              px: '10px',
              py: '3px',
              borderRadius: '12px',
            }}
          >
            {isPremium ? 'Premium' : (isProvided && 'Free Access') || '30 Days Free'}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 16, color: 'rgba(0,0,0,0.87)' }}>
            {providedPlanName || planName || 'Adopter Membership'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {isPremium ? 'Thank you for your support' : (isProvided && `Provided by ${providedBy}`) || 'Included with your adoption'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.625,
              fontWeight: 600,
              fontSize: 12.5,
              color: toneFg,
              bgcolor: toneBg,
              px: '11px',
              py: '4px',
              borderRadius: '9999px',
            }}
          >
            <Box
              sx={{
                width: 6, height: 6, borderRadius: '50%', bgcolor: toneFg,
              }}
            />
            {isExpired ? 'Expired' : 'Active'}
          </Box>
          {expiryDate && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {expiryLabel}
              {' '}
              {expiryDate}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Benefits */}
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontWeight: 600, fontSize: 13, color: 'rgba(0,0,0,0.87)', mb: 1,
          }}
        >
          Included in your plan
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 0.75,
          }}
        >
          {(isProvided ? COMMUNITY_BENEFITS : BENEFITS).map((b) => (
            <Box
              key={b}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, fontSize: 13.5, color: 'text.secondary',
              }}
            >
              <CheckIcon sx={{ fontSize: 17, color: ACTIVE_GREEN }} />
              {b}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Upgrade — free plan only */}
      {!isPremium && (
        <>
          <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.06)' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: 'rgba(0,0,0,0.87)' }}>
            Continue your access
          </Typography>
          <Typography
            sx={{
              fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5, mt: 0.625,
            }}
          >
            Keep access to trainer support and learning resources after your free membership ends.
          </Typography>
          <Box sx={{ mt: 1.75 }}>
            <Button
              variant="contained"
              href="https://petcademy.org/pricing/"
              target="_blank"
              rel="noopener"
              onClick={() => track('extend_membership_click', { from_where: 'settings' })}
              sx={{ width: '100%' }}
            >
              Upgrade Membership
            </Button>
          </Box>
        </>
      )}
    </SettingsCard>
  );
}

export default MembershipCard;
